import React, { useMemo } from 'react';
import { Modal } from '../ui/Modal';
import { safeArray } from '../../utils/gpsFormUtils';

// Uppercase TYPE badge labels (mockup variant).
const ANSWER_TYPE_LABELS = {
  choice: 'CHOIX UNIQUE',
  multichoice: 'MULTICHOIX',
  numeric: 'NUMÉRIQUE',
  date: 'DATE',
  text: 'TEXTE',
};

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('fr-FR');
};

// Group flat Q/A rows by questionId, preserving first-seen order.
const groupByQuestion = (rows) => {
  const order = [];
  const byId = new Map();
  rows.forEach((row) => {
    const key = row?.questionId ?? `__${order.length}`;
    if (!byId.has(key)) {
      byId.set(key, {
        questionId: key,
        questionLabel: typeof row?.questionLabel === 'string' ? row.questionLabel : '',
        answerType: row?.answerType,
        values: [],
      });
      order.push(key);
    }
    const v = row?.answerValue;
    if (v !== null && v !== undefined && v !== '') byId.get(key).values.push(String(v));
  });
  return order.map((key) => byId.get(key));
};

// Normalize a single-choice value to a boolean-ish kind for Oui/Non pills.
const booleanKind = (value) => {
  const v = String(value).trim().toLowerCase();
  if (['oui', 'yes', 'true', 'vrai'].includes(v)) return 'yes';
  if (['non', 'no', 'false', 'faux'].includes(v)) return 'no';
  return null;
};

const AnswerValues = ({ answerType, values }) => {
  if (values.length === 0) return <span className="text-gray-400 dark:text-gray-500">—</span>;

  // multichoice (or a choice with several values) → neutral grey chips.
  if (answerType === 'multichoice' || (answerType === 'choice' && values.length > 1)) {
    return (
      <div className="flex flex-wrap gap-2">
        {values.map((v, i) => (
          <span
            key={i}
            className="inline-flex px-2.5 py-0.5 rounded-full text-xs border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800"
          >
            {v}
          </span>
        ))}
      </div>
    );
  }

  // single choice → Oui/Non colored pills, otherwise plain text.
  if (answerType === 'choice') {
    const kind = booleanKind(values[0]);
    if (kind === 'yes') {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold text-emerald-700 dark:text-emerald-300 bg-emerald-600/10 border border-emerald-400/30">
          ● {values[0]}
        </span>
      );
    }
    if (kind === 'no') {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold text-red-700 dark:text-red-300 bg-red-600/10 border border-red-400/30">
          ● {values[0]}
        </span>
      );
    }
    return <p className="text-gray-800 dark:text-gray-200">{values.join(', ')}</p>;
  }

  if (answerType === 'text') {
    return <p className="italic text-gray-700 dark:text-gray-300">«&nbsp;{values.join(' ')}&nbsp;»</p>;
  }

  return <p className="text-gray-800 dark:text-gray-200">{values.join(', ')}</p>;
};

// Presentational per-form Q/A modal (form-grain "Voir le détail"). Defensive on
// missing fields; never throws on hostile payloads.
const GPSFormDetailModal = ({ isOpen, onClose, form, answers, loading }) => {
  const rows = safeArray(answers);
  const groups = useMemo(() => groupByQuestion(rows), [rows]);

  const issueKey = form?.issueKey || '—';
  const templateName =
    typeof form?.formTemplateName === 'string' && form.formTemplateName.trim() ? form.formTemplateName.trim() : '';
  // Title: "LODPGP-938 — [TECH] Installation" (em dash); fall back to just the key.
  const title = templateName ? `${issueKey} — ${templateName}` : issueKey;

  const sublineParts = [
    form?.interventionPoint || null,
    typeof form?.shopsign === 'string' && form.shopsign.trim() ? form.shopsign.trim() : null,
    formatDate(form?.answeredAt) || null,
    // Count distinct questions (matches the table's "Nb réponses"), not answer-rows
    // — a multichoice question yields several rows but is one answered question.
    `${groups.length} réponses`,
  ].filter(Boolean);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="m" style={{ maxWidth: 640 }}>
      <div className="space-y-4">
        <p className="text-sm text-gray-500 dark:text-gray-400">{sublineParts.join(' · ')}</p>

        {loading ? (
          <div className="animate-pulse space-y-3" data-testid="form-modal-loading">
            <div className="h-12 bg-gray-200 dark:bg-gray-800 rounded" />
            <div className="h-12 bg-gray-200 dark:bg-gray-800 rounded" />
            <div className="h-12 bg-gray-200 dark:bg-gray-800 rounded" />
          </div>
        ) : groups.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400 py-6 text-center">
            Aucune réponse pour ce formulaire.
          </p>
        ) : (
          <div className="max-h-[55vh] overflow-y-auto pr-1">
            {groups.map((group, i) => (
              <div
                key={group.questionId}
                className={`text-left space-y-1.5 py-4 ${
                  i < groups.length - 1 ? 'border-b border-gray-100 dark:border-gray-700' : ''
                }`}
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[11px] font-bold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                    {group.questionLabel || '—'}
                  </span>
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold tracking-wide bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300">
                    {ANSWER_TYPE_LABELS[group.answerType] || (group.answerType ? String(group.answerType).toUpperCase() : '—')}
                  </span>
                </div>
                <AnswerValues answerType={group.answerType} values={group.values} />
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default GPSFormDetailModal;
